export type RegionCode = 'TW-01' | 'TW-02' | 'TW-03' | 'TW-04' | 'TW-05' | 'JP-01'
export type InstanceType = 'CPU' | 'GPU'

export type AcceleratorVendor = 'NVIDIA' | 'INTEL'
export type AcceleratorCode =
  | 'NVIDIA_A100'
  | 'NVIDIA_GEFORCE_RTX_2080TI'
  | 'NVIDIA_GEFORCE_RTX_4090TI'
  | 'NVIDIA_H200'
  | 'NVIDIA_H100_SXM5'
  | 'INTEL_XEON_PLATINUM_8468'
  | 'INTEL_XEON_PLATINUM_8558'

export interface RegionPermissionItem {
  region: RegionCode
  enabled: boolean
}

export interface InstanceTypePermissionItem {
  type: InstanceType
  enabled: boolean
}

export interface AcceleratorPermissionItem {
  id: AcceleratorCode
  label: string
  vendor: AcceleratorVendor
  enabled: boolean
}

export interface InstanceRow {
  id: string
  checked: boolean
  gpuModel: string // e.g. 'NVIDIA A100'
  gpus: number
  gpuRamGB: number // 12
  region: RegionCode
  vcpus: number
  ramGB: number
  pricePerDayCredits: number // 999999.999
}

export type ImageOS = 'Windows' | 'Linux' | 'macOS'
export interface OfficialImage {
  id: string // img-xxxx
  name: string // 'Windows Pytorch 1.12'
  os: ImageOS
  details: string // 小字敘述
  enabled: boolean
}

export interface BillingTypePermission {
  subscription: boolean
  payg: boolean // pay-as-you-go
}

export interface InstancesQuota {
  memberInstanceQuota: number | null // 卡片左側輸入
  subscriptionTimeQuotaHour: number | null // 右上 Hour
  paygTimeQuotaHour: number | null // 下方 Hour
  teamDefaultPaygHour?: number | null // 用來顯示 "Team default: 300"
}

export interface OtherQuotas {
  snapshots: number | null
  storageSpaceGB: number | null
  autoDeploy: number | null
  autoScaling: number | null
  networkGroup: number | null
  publicIP: number | null
}

export interface Pagination {
  page: number
  pageSize: number
  total: number
}

export interface MemberPermissionMock {
  meta: {
    memberId: string
    username: string
    lastResetAt?: string | null
  }
  regions: RegionPermissionItem[]
  instanceTypes: InstanceTypePermissionItem[]
  accelerators: AcceleratorPermissionItem[]
  instanceTable: {
    rows: InstanceRow[]
    pagination: Pagination
  }
  officialImages: OfficialImage[]
  billing: BillingTypePermission
  instancesQuota: InstancesQuota
  otherQuotas: OtherQuotas
}

// ========== Mock Data ==========

export const memberPermissionMock: MemberPermissionMock = {
  meta: {
    memberId: 'mem_7k63',
    username: 'katie63',
    lastResetAt: null,
  },

  // Regions Permission
  regions: [
    { region: 'TW-01', enabled: true },
    { region: 'TW-02', enabled: false },
    { region: 'TW-03', enabled: false },
    { region: 'TW-04', enabled: true },
    { region: 'TW-05', enabled: false },
    { region: 'JP-01', enabled: true },
  ],

  // Instance Type Permission
  instanceTypes: [
    { type: 'CPU', enabled: true },
    { type: 'GPU', enabled: true },
  ],

  // Accelerator Permission (chips)
  accelerators: [
    { id: 'NVIDIA_A100', label: 'NVIDIA A100', vendor: 'NVIDIA', enabled: true },
    {
      id: 'NVIDIA_GEFORCE_RTX_2080TI',
      label: 'NVIDIA GeForce RTX 2080 Ti',
      vendor: 'NVIDIA',
      enabled: true,
    },
    {
      id: 'NVIDIA_GEFORCE_RTX_4090TI',
      label: 'NVIDIA GeForce RTX 4090 Ti',
      vendor: 'NVIDIA',
      enabled: false,
    },
    { id: 'NVIDIA_H200', label: 'NVIDIA H200', vendor: 'NVIDIA', enabled: false },
    { id: 'NVIDIA_H100_SXM5', label: 'NVIDIA H100 SXM5', vendor: 'NVIDIA', enabled: false },
    {
      id: 'INTEL_XEON_PLATINUM_8468',
      label: 'Intel(R) Xeon(R) Platinum 8468',
      vendor: 'INTEL',
      enabled: false,
    },
    {
      id: 'INTEL_XEON_PLATINUM_8558',
      label: 'INTEL(R) XEON(R) PLATINUM 8558',
      vendor: 'INTEL',
      enabled: false,
    },
  ],

  // Instance Permission table
  instanceTable: {
    rows: [
      {
        id: 'inst_a100_tw01_1',
        checked: true,
        gpuModel: 'NVIDIA A100',
        gpus: 1,
        gpuRamGB: 12,
        region: 'TW-01',
        vcpus: 0,
        ramGB: 64,
        pricePerDayCredits: 999999.999,
      },
      {
        id: 'inst_2080ti_tw01_1_a',
        checked: true,
        gpuModel: 'NVIDIA GeForce RTX 2080 Ti',
        gpus: 1,
        gpuRamGB: 12,
        region: 'TW-01',
        vcpus: 0,
        ramGB: 64,
        pricePerDayCredits: 999999.999,
      },
      {
        id: 'inst_2080ti_tw01_1_b',
        checked: false,
        gpuModel: 'NVIDIA GeForce RTX 2080 Ti',
        gpus: 1,
        gpuRamGB: 12,
        region: 'TW-01',
        vcpus: 0,
        ramGB: 64,
        pricePerDayCredits: 999999.999,
      },
      {
        id: 'inst_2080ti_tw01_1_c',
        checked: false,
        gpuModel: 'NVIDIA GeForce RTX 2080 Ti',
        gpus: 1,
        gpuRamGB: 12,
        region: 'TW-01',
        vcpus: 0,
        ramGB: 64,
        pricePerDayCredits: 999999.999,
      },
      {
        id: 'inst_2080ti_tw01_1_d',
        checked: false,
        gpuModel: 'NVIDIA GeForce RTX 2080 Ti',
        gpus: 1,
        gpuRamGB: 12,
        region: 'TW-01',
        vcpus: 0,
        ramGB: 64,
        pricePerDayCredits: 999999.999,
      },
    ],
    pagination: {
      page: 1,
      pageSize: 5,
      total: 13, // 1/3 頁的感覺
    },
  },

  // Official Image Permission (cards)
  officialImages: [
    {
      id: 'img-6yprgqw7',
      name: 'Windows Pytorch 1.12',
      os: 'Windows',
      details: 'Windows Server 2019, Python 3.8, Pytorch 1.12, CUDA 11.3, cuDNN 8.2…',
      enabled: true,
    },
    {
      id: 'img-3rtyuio2',
      name: 'Linux Pytorch 1.10',
      os: 'Linux',
      details: 'CentOS 7, Python 3.8, Pytorch 1.10, CUDA 11.0, cuDNN 8.1…',
      enabled: false,
    },
    {
      id: 'img-8hgjkpl1',
      name: 'Windows Pytorch 1.11',
      os: 'Windows',
      details: 'Windows 10, Python 3.7, Pytorch 1.11, CUDA 11.2, cuDNN 8.1…',
      enabled: false,
    },
    {
      id: 'img-6yprgqw7-2',
      name: 'Windows Pytorch 1.12',
      os: 'Windows',
      details: 'Windows Server 2019, Python 3.8, CUDA 11.3…(duplicate card in mock)',
      enabled: false,
    },
    {
      id: 'img-2jkqdwe5',
      name: 'Linux Pytorch 1.8',
      os: 'Linux',
      details: 'Ubuntu 20.04, Python 3.6, Pytorch 1.8, CUDA 11.0, cuDNN 8.0…',
      enabled: false,
    },
    {
      id: 'img-5nhbgf03',
      name: 'MacOS Pytorch 1.9',
      os: 'macOS',
      details: 'macOS Big Sur, Python 3.9, Pytorch 1.9, Metal Performance Shaders…',
      enabled: false,
    },
    {
      id: 'img-4thyig9f',
      name: 'Windows Pytorch 1.13',
      os: 'Windows',
      details: 'Windows 11, Python 3.9, Pytorch 1.13, CUDA 11.6, cuDNN 8.3.1…',
      enabled: false,
    },
  ],

  // Billing Type
  billing: {
    subscription: false,
    payg: true,
  },

  // Instances Quota
  instancesQuota: {
    memberInstanceQuota: 5,
    subscriptionTimeQuotaHour: 300,
    paygTimeQuotaHour: 500,
    teamDefaultPaygHour: 300, // 用來在 UI 顯示「Team default: 300」
  },

  // Others
  otherQuotas: {
    snapshots: 5,
    storageSpaceGB: 500,
    autoDeploy: 10,
    autoScaling: 10,
    networkGroup: 10,
    publicIP: 10,
  },
}

// 你也可以輸出一些「預設值」以支援 Reset to Default
export const defaultMemberPermission: Omit<MemberPermissionMock, 'meta'> = {
  regions: [
    { region: 'TW-01', enabled: true },
    { region: 'TW-02', enabled: true },
    { region: 'TW-03', enabled: true },
    { region: 'TW-04', enabled: true },
    { region: 'TW-05', enabled: false },
    { region: 'JP-01', enabled: false },
  ],
  instanceTypes: [
    { type: 'CPU', enabled: false },
    { type: 'GPU', enabled: true },
  ],
  accelerators: memberPermissionMock.accelerators.map((a) => ({ ...a, enabled: false })),
  instanceTable: { ...memberPermissionMock.instanceTable },
  officialImages: memberPermissionMock.officialImages.map((img, i) => ({
    ...img,
    enabled: i === 0,
  })),
  billing: { subscription: true, payg: false },
  instancesQuota: {
    memberInstanceQuota: 3,
    subscriptionTimeQuotaHour: 200,
    paygTimeQuotaHour: 300,
    teamDefaultPaygHour: 300,
  },
  otherQuotas: {
    snapshots: 3,
    storageSpaceGB: 200,
    autoDeploy: 5,
    autoScaling: 5,
    networkGroup: 5,
    publicIP: 5,
  },
}
